/**
 * VeterinairesPage - B2B Landing Page for Veterinarian Recruitment
 * Professional, institutional design targeting veterinarians
 * All content is managed from admin dashboard
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Stethoscope,
  Calendar,
  Video,
  FileText,
  Users,
  Shield,
  Clock,
  PhoneOff,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Rocket,
  Star,
  ChevronDown,
  Mail,
  Phone,
  Building2,
  Send,
  Heart,
  Zap,
  Globe,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icons
const iconMap = {
  Calendar,
  Video,
  FileText,
  PhoneOff,
  TrendingUp,
  Shield,
  Star,
  Heart,
  Zap,
  Globe,
  Award,
  Users,
  Clock,
  Stethoscope
};

const VeterinairesPage = () => {
  const [featuredCity, setFeaturedCity] = useState({
    name: "Nîmes",
    total_spots: 10,
    remaining_spots: 7
  });
  const [activeCities, setActiveCities] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [demoForm, setDemoForm] = useState({
    clinic_name: '',
    contact_name: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });

  // Page content from CMS
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch page content from API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/veterinaires`);
        if (response.ok) {
          const data = await response.json();
          setContent(data);
        }
      } catch (error) {
        console.debug('Using default content');
      }
      setLoading(false);
    };
    fetchContent();
  }, []);

  // Fetch featured city
  useEffect(() => {
    const fetchCityData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/founder-program/cities/featured`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedCity(data);
        }
      } catch (error) {
        console.debug('Using default city data');
      }
    };

    const fetchActiveCities = async () => {
      try {
        const response = await fetch(`${API_URL}/api/founder-program/cities/active`);
        if (response.ok) {
          const data = await response.json();
          setActiveCities(data);
        }
      } catch (error) {
        console.debug('Using default cities');
      }
    };

    fetchCityData();
    fetchActiveCities();
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    if (!demoForm.clinic_name || !demoForm.email || !demoForm.contact_name) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: demoForm.contact_name,
          email: demoForm.email,
          phone: demoForm.phone,
          subject: `Demande de démo - ${demoForm.clinic_name}`,
          message: `Clinique: ${demoForm.clinic_name}\nVille: ${demoForm.city}\n\n${demoForm.message}`,
          type: 'demo_request'
        })
      });

      if (response.ok) {
        toast.success(content?.demo_form?.success_message || 'Votre demande a été envoyée ! Nous vous contacterons rapidement.');
        setDemoForm({
          clinic_name: '',
          contact_name: '',
          email: '',
          phone: '',
          city: '',
          message: ''
        });
      } else {
        toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      toast.error('Erreur de connexion. Veuillez réessayer.');
    }
    
    setIsSubmitting(false);
  };

  // Get icon component from string name
  const getIcon = (iconName) => {
    return iconMap[iconName] || Star;
  };

  // Default content fallback
  const hero = content?.hero || {
    badge: "Espace Professionnel",
    title: "Modernisez votre",
    title_highlight: "clinique vétérinaire",
    subtitle: "La première plateforme française combinant prise de rendez-vous, carnet de santé partagé et téléconsultation.",
    cta_primary_text: "Demander une démo",
    cta_primary_link: "#demo",
    cta_secondary_text: "Se connecter",
    cta_secondary_link: "/espace-veterinaire"
  };

  const features = (content?.features || []).filter(f => f.active !== false);
  
  const founderProgram = content?.founder_program || {
    badge: "Programme exclusif",
    title: "Devenez une Clinique Fondatrice",
    description: "Nous sélectionnons un nombre limité de cliniques par ville pour co-construire la plateforme vétérinaire de demain.",
    benefits: [
      "6 mois d'accès privilégié gratuit",
      "Accompagnement personnalisé à la mise en place",
      "Tarifs fondateurs garantis à vie",
      "Participation au développement des fonctionnalités",
      "Support prioritaire dédié",
      "Formation gratuite de votre équipe"
    ],
    cta_text: "Rejoindre le programme"
  };

  const faq = content?.faq || { title: "Questions fréquentes", subtitle: "", items: [] };
  const faqItems = (faq.items || []).filter(f => f.active !== false);

  const demoFormContent = content?.demo_form || {
    title: "Planifier une démo",
    subtitle: "Découvrez comment vetoRDV peut transformer votre pratique quotidienne",
    submit_button_text: "Demander une démo gratuite",
    fields: {}
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,white_1px,transparent_1px)] bg-[length:30px_30px]"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Stethoscope className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-blue-400 font-semibold text-sm uppercase tracking-wide">
                    {hero.badge}
                  </span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  {hero.title}
                  <span className="text-blue-400"> {hero.title_highlight}</span>
                </h1>
                
                <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
                  {hero.subtitle}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={hero.cta_primary_link}
                    className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/25 text-lg"
                    data-testid="hero-demo-btn"
                  >
                    {hero.cta_primary_text}
                    <ArrowRight className="w-5 h-5" />
                  </a>
                  <Link
                    to={hero.cta_secondary_link}
                    className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white px-8 py-4 rounded-xl font-semibold transition-all border border-white/20"
                    data-testid="hero-login-btn"
                  >
                    {hero.cta_secondary_text}
                  </Link>
                </div>
              </div>
              
              {/* Founder Program Card */}
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-yellow-500/20 rounded-lg">
                      <Rocket className="w-5 h-5 text-yellow-400" />
                    </div>
                    <span className="text-yellow-400 font-semibold">Programme Clinique Fondatrice</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-2">
                    {featuredCity.name}
                  </h3>
                  <p className="text-slate-300 mb-6">
                    Rejoignez les {featuredCity.total_spots} cliniques sélectionnées
                  </p>
                  
                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-300">Places restantes</span>
                      <span className="text-2xl font-bold text-yellow-400">
                        {featuredCity.remaining_spots}/{featuredCity.total_spots}
                      </span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div 
                        className="bg-yellow-400 h-2 rounded-full transition-all"
                        style={{ width: `${(featuredCity.remaining_spots / featuredCity.total_spots) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {founderProgram.benefits.slice(0, 4).map((benefit, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-slate-200">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href="#demo"
                    className="w-full inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-yellow-900 px-6 py-3 rounded-xl font-bold transition-all"
                    data-testid="founder-cta-btn"
                  >
                    {founderProgram.cta_text} – {featuredCity.name}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
                
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-500 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-yellow-500 rounded-full opacity-20 blur-3xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        {features.length > 0 && (
          <section className="py-20 bg-slate-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                  Tout ce dont votre clinique a besoin
                </h2>
                <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                  Une solution complète pensée par et pour les vétérinaires
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map((feature, index) => {
                  const IconComponent = getIcon(feature.icon);
                  return (
                    <div 
                      key={feature.id || index}
                      className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow border border-slate-200 dark:border-slate-700"
                    >
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Founder Program Details */}
        <section className="py-20 bg-gradient-to-b from-blue-900 to-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <Rocket className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400 font-semibold">{founderProgram.badge}</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                  {founderProgram.title}
                </h2>
                
                <p className="text-lg text-slate-300 mb-8">
                  {founderProgram.description}
                </p>
                
                <ul className="space-y-4 mb-8">
                  {founderProgram.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-slate-200">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Active Cities */}
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  Villes du programme
                </h3>
                
                {activeCities.length > 0 ? (
                  <div className="space-y-4">
                    {activeCities.map((city) => (
                      <div 
                        key={city.city_id}
                        className="bg-white/10 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div>
                          <p className="font-semibold">{city.name}</p>
                          <p className="text-sm text-slate-300">
                            {city.remaining_spots} places sur {city.total_spots}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          city.remaining_spots > 0 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {city.remaining_spots > 0 ? 'Places disponibles' : 'Complet'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{featuredCity.name}</p>
                      <p className="text-sm text-slate-300">
                        {featuredCity.remaining_spots} places sur {featuredCity.total_spots}
                      </p>
                    </div>
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400">
                      Places disponibles
                    </div>
                  </div>
                )}
                
                <p className="text-sm text-slate-400 mt-6">
                  Votre ville n'est pas dans la liste ? Demandez une démo et nous vous tiendrons 
                  informé de l'ouverture dans votre région.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        {faqItems.length > 0 && (
          <section className="py-20 bg-white dark:bg-gray-900">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                  {faq.title}
                </h2>
                <p className="text-lg text-slate-600 dark:text-slate-300">
                  {faq.subtitle}
                </p>
              </div>
              
              <div className="space-y-4">
                {faqItems.map((faqItem, index) => (
                  <div 
                    key={faqItem.id || index}
                    className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between bg-slate-50 dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
                      data-testid={`faq-${index}`}
                    >
                      <span className="font-semibold text-slate-900 dark:text-white pr-4">
                        {faqItem.question}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    {expandedFaq === index && (
                      <div className="px-6 py-4 bg-white dark:bg-gray-900">
                        <p className="text-slate-600 dark:text-slate-300">
                          {faqItem.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Demo Form Section */}
        <section id="demo" className="py-20 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {demoFormContent.title}
              </h2>
              <p className="text-lg text-slate-300">
                {demoFormContent.subtitle}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
              <form onSubmit={handleDemoSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="clinic_name" className="text-slate-200">
                      {demoFormContent.fields?.clinic_name?.label || "Nom de la clinique *"}
                    </Label>
                    <div className="relative mt-1">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="clinic_name"
                        value={demoForm.clinic_name}
                        onChange={(e) => setDemoForm({...demoForm, clinic_name: e.target.value})}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        placeholder={demoFormContent.fields?.clinic_name?.placeholder || "Clinique Vétérinaire..."}
                        required
                        data-testid="demo-clinic-name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="contact_name" className="text-slate-200">
                      {demoFormContent.fields?.contact_name?.label || "Nom du contact *"}
                    </Label>
                    <div className="relative mt-1">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="contact_name"
                        value={demoForm.contact_name}
                        onChange={(e) => setDemoForm({...demoForm, contact_name: e.target.value})}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        placeholder={demoFormContent.fields?.contact_name?.placeholder || "Dr. ..."}
                        required
                        data-testid="demo-contact-name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-200">
                      {demoFormContent.fields?.email?.label || "Email *"}
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        value={demoForm.email}
                        onChange={(e) => setDemoForm({...demoForm, email: e.target.value})}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        placeholder={demoFormContent.fields?.email?.placeholder || "contact@clinique.fr"}
                        required
                        data-testid="demo-email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-200">
                      {demoFormContent.fields?.phone?.label || "Téléphone"}
                    </Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        type="tel"
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({...demoForm, phone: e.target.value})}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        placeholder={demoFormContent.fields?.phone?.placeholder || "06 12 34 56 78"}
                        data-testid="demo-phone"
                      />
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="city" className="text-slate-200">
                      {demoFormContent.fields?.city?.label || "Ville de votre clinique"}
                    </Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="city"
                        value={demoForm.city}
                        onChange={(e) => setDemoForm({...demoForm, city: e.target.value})}
                        className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        placeholder={demoFormContent.fields?.city?.placeholder || "Nîmes, Lyon, Paris..."}
                        data-testid="demo-city"
                      />
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="message" className="text-slate-200">
                      {demoFormContent.fields?.message?.label || "Message (optionnel)"}
                    </Label>
                    <Textarea
                      id="message"
                      value={demoForm.message}
                      onChange={(e) => setDemoForm({...demoForm, message: e.target.value})}
                      className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      placeholder={demoFormContent.fields?.message?.placeholder || "Parlez-nous de votre clinique..."}
                      rows={4}
                      data-testid="demo-message"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 text-lg font-semibold"
                  data-testid="demo-submit-btn"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {demoFormContent.submit_button_text}
                    </>
                  )}
                </Button>
                
                <p className="text-sm text-slate-400 text-center">
                  En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe. 
                  Vos données sont traitées conformément à notre{' '}
                  <Link to="/confidentialite" className="text-blue-400 hover:underline">
                    politique de confidentialité
                  </Link>.
                </p>
              </form>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default VeterinairesPage;
